import { AbstractControl, UntypedFormGroup } from "@angular/forms";
import {
	Ets1SessionColumnVM,
	Ets1SessionTreeItem,
	flattenEts1SessionTree,
	hasEts1RadioChildren,
} from "./ets1-session-layout.utils";

export function hasMissingRequiredSessionText(
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup,
	columns: Ets1SessionColumnVM[],
): boolean {
	const selected = sessions.getRawValue() as Record<string, boolean>;
	const items = flattenEts1SessionTree(columns);

	return items.some(
		(item) => item.requiresText && selected[item.code] && !String(sessionRemarks.get(item.code)?.value ?? "").trim(),
	);
}

export function missingRequiredSessionText(
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup,
	columns: Ets1SessionColumnVM[],
): boolean {
	const selected = sessions.getRawValue() as Record<string, boolean>;
	const items = flattenEts1SessionTree(columns);
	let missing = false;

	for (const item of items) {
		const ctrl = sessionRemarks.get(item.code);
		if (!item.requiresText || !selected[item.code]) {
			clearControlError(ctrl, "required");
			continue;
		}
		if (!String(ctrl?.value ?? "").trim()) {
			ctrl?.setErrors({ required: true });
			ctrl?.markAsTouched();
			missing = true;
		} else if (ctrl?.hasError("required")) {
			clearControlError(ctrl, "required");
		}
	}

	return missing;
}

export function hasSessionRemarkRequiredErrors(sessionRemarks: UntypedFormGroup): boolean {
	return Object.values(sessionRemarks.controls).some((ctrl) => ctrl.hasError("required") && ctrl.touched);
}

export function sessionRemarkRequiredError(code: string, sessionRemarks: UntypedFormGroup): boolean {
	const ctrl = sessionRemarks.get(code);
	return !!ctrl && ctrl.hasError("required") && ctrl.touched;
}

export function wireEts1RadioGroupExclusion(
	parentCode: string,
	childCodes: string[],
	sessions: UntypedFormGroup,
	subs: Array<{ unsubscribe: () => void }>,
): void {
	if (!childCodes.length) return;

	const parent = sessions.get(parentCode);
	const setChild = (activeCode: string | null) => {
		for (const code of childCodes) {
			const ctrl = sessions.get(code);
			ctrl?.setValue(code === activeCode, { emitEvent: false });
		}
		if (activeCode && parent && !parent.value) {
			parent.setValue(true, { emitEvent: false });
		}
	};

	for (const code of childCodes) {
		const ctrl = sessions.get(code);
		if (!ctrl) continue;
		const sub = ctrl.valueChanges.subscribe((checked: boolean) => {
			if (!checked) return;
			for (const other of childCodes) {
				if (other === code) continue;
				sessions.get(other)?.setValue(false, { emitEvent: false });
			}
			if (parent && !parent.value) {
				parent.setValue(true, { emitEvent: false });
			}
		});
		subs.push(sub);
	}

	if (parent) {
		const parentSub = parent.valueChanges.subscribe((checked: boolean) => {
			if (!checked) {
				setChild(null);
				clearControlError(parent, "requiredRadio");
			}
		});
		subs.push(parentSub);
	}
}

function clearTreeItemSelection(
	item: Ets1SessionTreeItem,
	sessions: UntypedFormGroup,
	sessionRemarks?: UntypedFormGroup | null,
): void {
	const ctrl = sessions.get(item.code);
	ctrl?.setValue(false, { emitEvent: false });
	clearControlError(ctrl, "requiredRadio");

	const remark = sessionRemarks?.get(item.code);
	remark?.setValue("", { emitEvent: false });
	clearControlError(remark, "required");

	for (const child of item.children) {
		clearTreeItemSelection(child, sessions, sessionRemarks);
	}
}

export function enforceEts1SingleRootSelection(
	columns: Ets1SessionColumnVM[],
	sessions: UntypedFormGroup,
	sessionRemarks?: UntypedFormGroup | null,
): void {
	const roots = columns.flatMap((column) => column.items);
	let activeRootCode: string | null = null;

	for (const item of roots) {
		const isSelected = !!sessions.get(item.code)?.value;
		if (!isSelected) continue;
		if (!activeRootCode) {
			activeRootCode = item.code;
		} else {
			clearTreeItemSelection(item, sessions, sessionRemarks);
		}
	}
}

export function wireEts1RootSingleSelection(
	columns: Ets1SessionColumnVM[],
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup | null | undefined,
	subs: Array<{ unsubscribe: () => void }>,
): void {
	const roots = columns.flatMap((column) => column.items);
	if (roots.length <= 1) return;

	const deactivateOtherRoots = (activeRootCode: string) => {
		for (const item of roots) {
			if (item.code === activeRootCode) continue;
			clearTreeItemSelection(item, sessions, sessionRemarks);
		}
	};

	for (const item of roots) {
		const rootCtrl = sessions.get(item.code);
		if (rootCtrl) {
			subs.push(
				rootCtrl.valueChanges.subscribe((checked: boolean) => {
					if (checked) deactivateOtherRoots(item.code);
				}),
			);
		}

		for (const child of item.children) {
			const childCtrl = sessions.get(child.code);
			if (!childCtrl) continue;
			subs.push(
				childCtrl.valueChanges.subscribe((checked: boolean) => {
					if (checked) deactivateOtherRoots(item.code);
				}),
			);
		}
	}
}

export function clearControlError(ctrl: AbstractControl | null | undefined, key: string): void {
	if (!ctrl?.errors?.[key]) return;
	const rest = { ...ctrl.errors };
	delete rest[key];
	ctrl.setErrors(Object.keys(rest).length ? rest : null);
}

export function clearSessionRemarkWhenUnchecked(
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup,
	code: string,
	subs: Array<{ unsubscribe: () => void }>,
): void {
	const sessionCtrl = sessions.get(code);
	if (!sessionCtrl) return;
	const sub = sessionCtrl.valueChanges.subscribe((checked: boolean) => {
		if (!checked) {
			const remark = sessionRemarks.get(code);
			remark?.setValue("", { emitEvent: false });
			clearControlError(remark, "required");
			clearControlError(sessionCtrl, "requiredRadio");
		}
	});
	subs.push(sub);
}

export function hasMissingRequiredEts1RadioSelection(
	sessions: UntypedFormGroup,
	columns: Ets1SessionColumnVM[],
): boolean {
	const selected = sessions.getRawValue() as Record<string, boolean>;
	return flattenEts1SessionTree(columns).some((item) => {
		if (!hasEts1RadioChildren(item) || !selected[item.code]) return false;
		return !item.children.some((child) => selected[child.code]);
	});
}

export function markMissingRequiredEts1RadioSelection(
	sessions: UntypedFormGroup,
	columns: Ets1SessionColumnVM[],
): boolean {
	const selected = sessions.getRawValue() as Record<string, boolean>;
	let missing = false;

	for (const item of flattenEts1SessionTree(columns)) {
		const parentCtrl = sessions.get(item.code);
		clearControlError(parentCtrl, "requiredRadio");

		if (!hasEts1RadioChildren(item) || !selected[item.code]) continue;
		if (item.children.some((child) => selected[child.code])) continue;
		parentCtrl?.setErrors({ ...(parentCtrl.errors ?? {}), requiredRadio: true });
		parentCtrl?.markAsTouched();
		missing = true;
	}

	return missing;
}

export function ets1RadioRequiredError(code: string, sessions: UntypedFormGroup): boolean {
	const ctrl = sessions.get(code);
	return !!ctrl && ctrl.hasError("requiredRadio") && ctrl.touched;
}

export function hasEts1RadioRequiredErrors(sessions: UntypedFormGroup): boolean {
	return Object.values(sessions.controls).some((ctrl) => ctrl.hasError("requiredRadio") && ctrl.touched);
}

export function markAllSessionControlsTouched(control: AbstractControl): void {
	if (control instanceof UntypedFormGroup) {
		Object.values(control.controls).forEach((child) => markAllSessionControlsTouched(child));
	}
	control.markAsTouched();
}
